import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Empty } from "@/components/Bits";
import { money, hhmm, marginClass, canSeeCost, date } from "@/lib/format";
import { AlertTriangle, Boxes, Timer, ShoppingCart, ReceiptText, TrendingDown, Clock as ClockIcon } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const cost = canSeeCost(user.role);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return <div className="p-6 text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Operations Dashboard" subtitle="Live view of jobs, labour, purchasing and margins" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-open-jobs" label="Open Jobs" value={d.open_jobs} sub={`${d.total_jobs} total`} />
        <StatCard testid="stat-late-jobs" label="Late Jobs" value={d.late_jobs.length} accent={d.late_jobs.length ? "text-red-400" : "text-emerald-400"} sub="past due date" />
        <StatCard testid="stat-blocked" label="Blocked Tasks" value={d.blocked_stages} accent={d.blocked_stages ? "text-amber-400" : "text-foreground"} sub="on hold / rework" />
        <StatCard testid="stat-unalloc" label="Unallocated Today" value={hhmm(d.unallocated_minutes_today)} accent={d.unallocated_minutes_today ? "text-amber-400" : "text-emerald-400"} sub="clocked, not booked" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Late Jobs" icon={AlertTriangle}>
          {d.late_jobs.length === 0 ? <Empty text="No late jobs 🎉" /> : (
            <ul className="divide-y divide-border">
              {d.late_jobs.map((j) => (
                <li key={j.id} className="py-2.5 flex items-center justify-between">
                  <Link to={`/jobs/${j.id}`} className="text-sm hover:underline flex-1 truncate"><span className="text-muted-foreground">{j.customer_name} · </span><span className="font-semibold">{j.job_name || j.job_number}</span></Link>
                  <span className="text-xs text-red-400">due {date(j.due_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Task Bottlenecks" icon={Boxes}>
          {d.bottlenecks.length === 0 ? <Empty text="No active stages" /> : (
            <ul className="space-y-2">
              {d.bottlenecks.slice(0, 6).map((b) => (
                <li key={b.stage} className="flex items-center gap-3">
                  <span className="text-sm font-semibold w-28 truncate">{b.stage}</span>
                  <div className="flex-1 flex gap-1 text-[11px] font-mono">
                    {b.blocked > 0 && <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300">{b.blocked} blocked</span>}
                    {b.in_progress > 0 && <span className="px-2 py-0.5 rounded bg-sky-500/20 text-sky-300">{b.in_progress} active</span>}
                    {b.awaiting > 0 && <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">{b.awaiting} waiting</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Approvals Queue" icon={ShoppingCart}>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/purchasing" className="block p-4 rounded-lg bg-accent/40 hover:bg-accent transition-colors" data-testid="link-pending-po">
              <div className="font-heading text-3xl font-extrabold">{d.pending_po}</div>
              <div className="text-xs text-muted-foreground mt-1">Purchase orders awaiting approval</div>
            </Link>
            <Link to="/ready-to-invoice" className="block p-4 rounded-lg bg-accent/40 hover:bg-accent transition-colors" data-testid="link-rti">
              <div className="font-heading text-3xl font-extrabold flex items-center gap-2">{d.rti_pending}<ReceiptText className="w-5 h-5 text-primary" /></div>
              <div className="text-xs text-muted-foreground mt-1">Items ready to invoice</div>
            </Link>
          </div>
        </Card>

        {cost && (
          <Card title="Job Margins (lowest first)" icon={TrendingDown}>
            {d.job_margins.length === 0 ? <Empty text="No open jobs" /> : (
              <ul className="divide-y divide-border">
                {d.job_margins.map((m) => (
                  <li key={m.id} className="py-2.5 flex items-center justify-between gap-2">
                    <Link to={`/jobs/${m.id}`} className="text-sm hover:underline flex-1 truncate"><span className="text-muted-foreground text-xs">{m.customer_name} · </span><span className="font-semibold">{m.job_name || m.job_number}</span></Link>
                    <span className="text-sm font-mono-num">{money(m.contract_value)}</span>
                    <span className={`text-sm font-mono-num font-bold w-16 text-right ${marginClass(m.margin_pct)}`}>{m.margin_pct}%</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
        {!cost && (
          <Card title="Your Role" icon={Timer}>
            <p className="text-sm text-muted-foreground">Cost and margin figures are restricted. You can view production, schedule and time.</p>
          </Card>
        )}
      </div>

      <HoursSection />
    </div>
  );
}

function HoursSection() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/dashboard/hours").then((r) => setH(r.data)).catch(() => {}); }, []);
  if (!h) return null;
  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <ClockIcon className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold uppercase tracking-wide text-sm">Hours on Open Jobs — Worked vs Billed</h3>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <StatCard testid="hours-worked" label="Hours Worked" value={`${h.total_worked}h`} />
        <StatCard testid="hours-billed" label="Hours Billed" value={`${h.total_billed}h`} />
        <StatCard testid="hours-variance" label="Unbilled (Worked − Billed)" value={`${h.total_variance}h`} accent={h.total_variance > 0 ? "text-amber-400" : "text-emerald-400"} />
      </div>
      <div className="card-industrial overflow-hidden" data-testid="hours-table">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Customer / Job</th><th className="text-left px-4 py-3">Code</th>
            <th className="text-right px-4 py-3">Worked</th><th className="text-right px-4 py-3">Billed</th><th className="text-right px-4 py-3">Unbilled</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {h.rows.map((r) => (
              <tr key={r.job_id}>
                <td className="px-4 py-3"><Link to={`/jobs/${r.job_id}`} className="hover:underline"><span className="text-muted-foreground">{r.customer_name} · </span><span className="font-semibold">{r.job_name || r.job_number}</span></Link></td>
                <td className="px-4 py-3 text-muted-foreground font-mono-num text-xs">{r.job_number}</td>
                <td className="px-4 py-3 text-right font-mono-num">{r.worked_hours}h</td>
                <td className="px-4 py-3 text-right font-mono-num">{r.billed_hours}h</td>
                <td className={`px-4 py-3 text-right font-mono-num font-bold ${r.variance_hours > 0 ? "text-amber-400" : "text-emerald-400"}`}>{r.variance_hours}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="card-industrial p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold uppercase tracking-wide text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}
